import { createContext, useContext, useState } from 'react';
import { Link, useHref } from 'react-router-dom';
import { gql } from 'graphql-request';
import { useMutation } from '@tanstack/react-query';

import styles from './Subscriptions.module.css';

import config from '../../../config';
import { Icon } from '../../../components/icon/Icon';
import { Loading } from '../../../components/loading/Loading';
import { useBackUrl } from '../../../App.hooks';
import { NavbarIconLogo } from '@/constants';
import { Price, Product, useProducts } from './Subscription.hook';
import { AuthCognito } from '../../../config.type';
import { AppContext } from '../../../App.context';
import { IS_AUTHENTICATED_KEY } from '@/auth/constants';

export function Subscriptions() {
	const [priceId, setPriceId] = useState<string | null>(null);
	return (
		<SubscriptionContext.Provider
			value={{
				priceId,
				setPriceId,
			}}
		>
			<div className={styles['container']}>
				<View />
			</div>
		</SubscriptionContext.Provider>
	);
}

const SubscriptionContext = createContext<SubscriptionContextProps>({
	priceId: null,
	setPriceId: () => {},
});

interface SubscriptionContextProps {
	priceId: string | null;
	setPriceId: (i: string) => void;
}

function View() {
	const isAuthenticated = localStorage.getItem(IS_AUTHENTICATED_KEY);
	return (
		<div className={styles['view']}>
			<Topbar />
			{isAuthenticated ? <Content /> : <AuthRequired />}
		</div>
	);
}

function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div></div>
		</div>
	);
}

function BackButton() {
	const url = useBackUrl();
	return (
		<button id="back-button" type="button" title="Back">
			<Link to={url}>
				<Icon id={'back-btn'} name={'arrow-left'} color={'#333'} />
			</Link>
		</button>
	);
}

function Content() {
	const { products, isLoading, error } = useProducts();
	if (isLoading) {
		return (
			<div className={styles['content']}>
				<Loading />
			</div>
		);
	}

	if (error) {
		return (
			<div className={[styles['content'], styles['error']].join(' ')}>
				{error.message}
			</div>
		);
	}

	if (!products.length) {
		return (
			<div className={[styles['content'], styles['empty']].join(' ')}>
				Subscriptions not found
			</div>
		);
	}

	return (
		<div className={styles['content']}>
			<Products products={products} />
		</div>
	);
}

function AuthRequired() {
	const basename = useHref('/callback');
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}
	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	return (
		<div className={styles['content']} style={{ color: 'black' }}>
			<div className={styles['auth']}>
				<h1>Sign In Required</h1>
				<h2>Authentication is required to purchase a subscription</h2>
				<button
					type={'button'}
					onClick={() => {
						location.href = redirectUrl;
					}}
				>
					Sign in
				</button>
			</div>
		</div>
	);
}

function Products({ products }: ProductsProps) {
	const basename = useHref('/subscriptions');
	const { graphQLClient } = useContext(AppContext);

	const { priceId } = useContext(SubscriptionContext);
	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const { mutate, isPending } = useMutation<{ url: string }>({
		mutationFn: async () =>
			graphQLClient.request(checkoutMutationDocument, {
				priceId,
				successUrl: `${redirectUri}/success`,
				cancelUrl: `${redirectUri}`,
			}),
		onError: err => {
			console.error(err);
		},
		onSuccess: data => {
			if (!data) {
				return;
			}

			const { url } = data;
			location.href = url;
		},
	});

	const onSubmit = (e: any) => {
		e.preventDefault();

		if (!priceId) {
			return;
		}

		mutate();
	};
	return (
		<form className={styles['products']} onSubmit={onSubmit}>
			{products.map(p => (
				<ProductComponent key={p.id} {...p} />
			))}
			<footer>
				<CheckoutBtn isLoading={isPending} />
				<ManageSubscription />
			</footer>
		</form>
	);
}

function ManageSubscription() {
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	const auth = config.app.auth as AuthCognito;
	const { customerPortalUrl } = auth.config;
	if (!customerPortalUrl) {
		return null;
	}
	return (
		<button
			type={'button'}
			onClick={() => {
				const r = open(customerPortalUrl, '_blank');
				if (r !== null) {
					r.focus();
				}
			}}
		>
			Manage Subscription
		</button>
	);
}

function CheckoutBtn({ isLoading }: { isLoading: boolean }) {
	const { priceId } = useContext(SubscriptionContext);
	if (isLoading) {
		return (
			<button type={'submit'} disabled>
				Processing
			</button>
		);
	}

	if (!priceId) {
		return (
			<button type={'submit'} disabled>
				Checkout
			</button>
		);
	}

	return <button type={'submit'}>Checkout</button>;
}

interface ProductsProps {
	products: Array<Product>;
}

function ProductComponent(product: Product) {
	const { name, prices, images, description } = product;
	const image = images.length ? images[0] : null;

	return (
		<fieldset>
			<div className={styles['title']}>
				{image ? <img src={image} alt={'Cover'} /> : null}
				<legend>{name}</legend>
			</div>
			{description && (
				<div className={styles['description']}>{description}</div>
			)}

			{prices.map(p => (
				<PriceComponent key={p.id} {...p} />
			))}
		</fieldset>
	);
}

function PriceComponent(price: Price) {
	const { priceId, setPriceId } = useContext(SubscriptionContext);
	const { id, currency, unitAmount, recurring } = price;
	if (recurring == null) {
		return null;
	}

	return (
		<span
			role="presentation"
			className={styles['price']}
			onClick={() => {
				setPriceId(id);
			}}
		>
			<input
				type="radio"
				id={`price-${id}`}
				name="price_id"
				checked={priceId === id}
				value={id}
				onChange={e => {
					setPriceId(e.target.value);
				}}
			/>
			<label htmlFor={`price-${id}`}>{getPriceTitle(price)}</label>
			<h4>
				{`${getCurrencySymbol(currency)}${parseFloat(
					`${(unitAmount === null ? 0 : unitAmount) / 100}`,
				).toFixed(2)}`}
			</h4>
		</span>
	);
}

function getPriceTitle(price: Price): string {
	const { nickname, recurring } = price;
	if (!recurring) {
		return '';
	}

	const { interval, intervalCount } = recurring;
	if (nickname) {
		return nickname;
	}

	if (intervalCount === 1) {
		switch (interval) {
			case 'day':
				return 'Daily';
			case 'month':
				return 'Monthly';
			case 'week':
				return 'Weekly';
			case 'year':
				return 'Yearly';
			default:
				return `Every ${interval}`;
		}
	}

	return `Every ${intervalCount} ${interval}`;
}

function getCurrencySymbol(currency: string): string {
	if (!currency) {
		return currency;
	}
	currency = currency.toLowerCase();
	switch (currency) {
		case 'usd':
			return '$';
		case 'gbp':
			return '£';
		default:
			currency;
	}

	return currency;
}

const checkoutMutationDocument = gql`
	mutation CreateCheckoutSession(
		$priceId: String!
		$successUrl: String!
		$cancelUrl: String!
	) {
		url: createCheckoutSession(
			priceId: $priceId
			successUrl: $successUrl
			cancelUrl: $cancelUrl
		)
	}
`;
